import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/intl";
import { Plus, KeyRound, Power, Trash2, UserCog, ShieldCheck } from "lucide-react";

const ROLES = [
  { value: "owner", label: "Owner", desc: "Full access incl. user management" },
  { value: "manager", label: "Manager", desc: "Can run jobs, receipts, reports" },
  { value: "staff", label: "Staff", desc: "View-only + appointment check-ins" },
];

function RoleBadge({ role }) {
  const tone =
    role === "owner"
      ? "bg-zinc-950 text-white"
      : role === "manager"
      ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
      : "bg-zinc-100 text-zinc-800 border border-zinc-200";
  return <Badge className={`uppercase text-2xs tracking-wider ${tone}`}>{role}</Badge>;
}

export default function Users() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [resetUser, setResetUser] = useState(null);

  // Create form state
  const [cEmail, setCEmail] = useState("");
  const [cName, setCName] = useState("");
  const [cPassword, setCPassword] = useState("");
  const [cRole, setCRole] = useState("staff");
  const [submitting, setSubmitting] = useState(false);

  // Reset password form state
  const [resetPw, setResetPw] = useState("");

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/users");
      setRows(r.data || []);
    } catch (e) {
      toast.error("Could not load users", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function createUser(e) {
    e.preventDefault();
    if (!cEmail || !cName || cPassword.length < 8) {
      toast.error("Email, name, and a password of 8+ characters are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/users", {
        email: cEmail.trim(),
        name: cName.trim(),
        password: cPassword,
        role: cRole,
      });
      toast.success("User created");
      setCEmail(""); setCName(""); setCPassword(""); setCRole("staff");
      setCreating(false);
      load();
    } catch (e) {
      toast.error("Could not create user", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSubmitting(false);
    }
  }

  async function setRole(u, role) {
    try {
      await api.patch(`/users/${u.id}`, { role });
      toast.success(`Role updated for ${u.email}`);
      load();
    } catch (e) {
      toast.error("Could not update role", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function toggleDisabled(u) {
    try {
      await api.patch(`/users/${u.id}`, { disabled: !u.disabled });
      toast.success(u.disabled ? "User re-enabled" : "User disabled");
      load();
    } catch (e) {
      toast.error("Could not update", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function removeUser(u) {
    if (!window.confirm(`Permanently delete ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error("Could not delete user", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function submitResetPassword(e) {
    e.preventDefault();
    if (resetPw.length < 8) {
      toast.error("Password must be 8+ characters");
      return;
    }
    try {
      await api.post(`/users/${resetUser.id}/reset-password`, { new_password: resetPw });
      toast.success(`Password reset for ${resetUser.email}`);
      setResetUser(null);
      setResetPw("");
      load();
    } catch (e) {
      toast.error("Could not reset password", { description: e?.response?.data?.detail || e.message });
    }
  }

  return (
    <div className="space-y-8" data-testid="users-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          Access control
        </div>
        <h1 className="mt-2 font-display text-4xl sm:text-5xl font-bold tracking-tight text-zinc-950">
          Users & roles
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-zinc-600">
          Owners can create new accounts, change roles, disable access, or reset passwords. Each
          new user is forced to set their own password on first login.
        </p>
      </div>

      <Card className="p-6 sm:p-8 rounded-lg border border-zinc-200 shadow-none bg-zinc-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            <UserCog className="h-3.5 w-3.5" /> Team
          </div>
          <Button
            onClick={() => setCreating(true)}
            data-testid="users-add-button"
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Add user
          </Button>
        </div>

        <div className="mt-6 overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Email</th>
                <th className="py-2 pr-4">Role</th>
                <th className="py-2 pr-4" title="Can approve Clinical Resource Library content">Clinical approver</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Last login</th>
                <th className="py-2 pr-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="py-4 text-zinc-500">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="py-4 text-zinc-500">No users yet.</td></tr>
              )}
              {rows.map((u) => {
                const isSelf = me?.id === u.id;
                return (
                  <tr key={u.id} className="border-b border-zinc-100 align-middle" data-testid={`user-row-${u.id}`}>
                    <td className="py-3 pr-4 text-zinc-900">{u.name || "—"}{isSelf && <span className="ml-1 text-xs text-zinc-500">(you)</span>}</td>
                    <td className="py-3 pr-4 font-mono-num text-zinc-700">{u.email}</td>
                    <td className="py-3 pr-4">
                      <Select
                        value={u.role}
                        onValueChange={(v) => setRole(u, v)}
                        disabled={isSelf}
                      >
                        <SelectTrigger className="h-8 w-[110px] bg-white" data-testid={`user-role-${u.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ROLES.map((r) => (
                            <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-3 pr-4">
                      <ApproverToggle user={u} onChanged={load} />
                    </td>
                    <td className="py-3 pr-4">
                      {u.disabled ? (
                        <Badge className="bg-red-100 text-red-800 border border-red-200">disabled</Badge>
                      ) : u.must_change_password ? (
                        <Badge className="bg-amber-100 text-amber-800 border border-amber-200">pw reset</Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">active</Badge>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-zinc-700">{u.last_login_at ? fmtDateTime(u.last_login_at) : "—"}</td>
                    <td className="py-3 pr-4">
                      <div className="flex items-center gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8"
                          data-testid={`user-reset-${u.id}`}
                          onClick={() => { setResetUser(u); setResetPw(""); }}
                        >
                          <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8"
                          disabled={isSelf}
                          data-testid={`user-toggle-${u.id}`}
                          onClick={() => toggleDisabled(u)}
                        >
                          <Power className="h-3.5 w-3.5 mr-1" /> {u.disabled ? "Enable" : "Disable"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 text-red-700 hover:bg-red-50"
                          disabled={isSelf}
                          data-testid={`user-delete-${u.id}`}
                          onClick={() => removeUser(u)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-6 rounded-lg border border-zinc-200 shadow-none">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
          <ShieldCheck className="h-3.5 w-3.5" /> Role guide
        </div>
        <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
          {ROLES.map((r) => (
            <div key={r.value} className="rounded-md border border-zinc-200 p-3 bg-zinc-50">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-zinc-950">{r.label}</span>
                <RoleBadge role={r.value} />
              </div>
              <p className="mt-1 text-xs text-zinc-600">{r.desc}</p>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="users-add-dialog">
          <DialogHeader>
            <DialogTitle>Add a new user</DialogTitle>
          </DialogHeader>
          <form onSubmit={createUser} className="space-y-4">
            <div>
              <Label htmlFor="nu-name">Full name</Label>
              <Input
                id="nu-name"
                data-testid="add-user-name"
                value={cName}
                onChange={(e) => setCName(e.target.value)}
                className="mt-1.5 bg-white"
              />
            </div>
            <div>
              <Label htmlFor="nu-email">Email</Label>
              <Input
                id="nu-email"
                data-testid="add-user-email"
                type="email"
                value={cEmail}
                onChange={(e) => setCEmail(e.target.value)}
                className="mt-1.5 bg-white"
              />
            </div>
            <div>
              <Label htmlFor="nu-pw">Temporary password</Label>
              <Input
                id="nu-pw"
                data-testid="add-user-password"
                type="text"
                value={cPassword}
                onChange={(e) => setCPassword(e.target.value)}
                className="mt-1.5 bg-white font-mono-num"
              />
              <p className="mt-1.5 text-xs text-zinc-500">
                The user will be forced to change this on first sign-in.
              </p>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={cRole} onValueChange={setCRole}>
                <SelectTrigger className="mt-1.5 bg-white" data-testid="add-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label} — <span className="text-xs text-zinc-500">{r.desc}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreating(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="add-user-submit"
                className="bg-zinc-950 hover:bg-zinc-800 text-white"
              >
                {submitting ? "Creating…" : "Create user"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetUser} onOpenChange={(v) => { if (!v) setResetUser(null); }}>
        <DialogContent data-testid="users-reset-dialog">
          <DialogHeader>
            <DialogTitle>Reset password for {resetUser?.email}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitResetPassword} className="space-y-4">
            <div>
              <Label htmlFor="rp-new">New password</Label>
              <Input
                id="rp-new"
                data-testid="reset-pw-input"
                type="text"
                value={resetPw}
                onChange={(e) => setResetPw(e.target.value)}
                className="mt-1.5 bg-white font-mono-num"
              />
              <p className="mt-1.5 text-xs text-zinc-500">
                The user will be forced to set their own password on next sign-in.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setResetUser(null)}>Cancel</Button>
              <Button
                type="submit"
                data-testid="reset-pw-submit"
                className="bg-zinc-950 hover:bg-zinc-800 text-white"
              >
                Reset password
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ApproverToggle({ user: u, onChanged }) {
  const always = u.role === "owner";
  const blocked = u.role === "auditor";
  async function toggle(v) {
    try {
      await api.patch(`/users/${u.id}`, { clinical_approver: v });
      toast.success(v ? `${u.name || u.email} can now approve clinical resources` : "Clinical approval removed");
      onChanged();
    } catch (e) {
      toast.error("Could not update", { description: e?.response?.data?.detail || e.message });
    }
  }
  return (
    <div className="flex items-center gap-2" title={always ? "Owners can always approve" : blocked ? "Auditors are read-only" : ""}>
      <Switch checked={always || !!u.clinical_approver} disabled={always || blocked} onCheckedChange={toggle}
              data-testid={`user-approver-${u.id}`} />
      {always && <span className="text-2xs text-zinc-500">always</span>}
    </div>
  );
}
